import {createContext, useContext} from 'react'
import * as lengths from '@/script/moduleLen.js'

export class NodePropModel {
    static create(map, nodeDef, prop, node) {
        const propDef = nodeDef?.[prop]

        if (! propDef || ! node) {
            return null
        }

        const PropModel = {
            key: NodePropKeyModel,
            string: NodePropStringModel,
            text: NodePropStringModel,
            number: NodePropNumberModel,
            boolean: NodePropBooleanModel,
        }[propDef.type]

        return new PropModel(map, propDef, prop, node)
    }

    constructor(map, definition, prop, node) {
        this._definition = definition
        this._map = map
        this._node = node
        this.#prop = prop
    }

    get isDisabled() {
        return this._definition.disabled?.(this._map, this._node) ?? false
    }

    get isHidden() {
        return this._definition.hidden?.(this._map, this._node) ?? false
    }

    get isReadOnly() {
        return this._definition.readOnly?.(this._map, this._node) ?? false
    }

    get isRequired() {
        return this._definition.required?.(this._map, this._node) ?? false
    }

    get name() {
        return this._definition.name?.(this._map, this._node) ?? ''
    }

    get raw() {
        return this._node.data[this.#prop]
    }

    get type() {
        return this._definition.type
    }

    get value() {
        return this.raw
    }

    validate() {
        if (
            this.isRequired &&
            ! this.isHidden &&
            ! this.isDisabled &&
            ! this.isReadOnly &&
            ! this._definition.ignoreRequiredInValidation &&
            ! this.raw &&
            0 !== this.raw
        ) {
            return `${this.name}不能为空`
        }
        else {
            return this._definition.validate?.(this._node)
        }
    }

    _definition = null
    _map = null
    _node = null
    #prop = ''
}

class NodePropBooleanModel extends NodePropModel {
    get value() {
        const {
            raw,
            _definition: {trueValue = '1'}
        } = this

        return raw === trueValue
    }
}

class NodePropNumberModel extends NodePropModel {
    get value() {
        const {raw} = this

        if (raw || 0 === raw) {
            return Number(this.raw)
        }
        else {
            return NaN
        }
    }
}

class NodePropStringModel extends NodePropModel {
    get value() {
        return this.raw ?? ''
    }
}

class NodePropKeyModel extends NodePropStringModel {
    get text() {
        return this._definition.text?.(this._map, this._node) ?? ''
    }
}

const bizNodeModel = (definition = {}) => {
    return class NodeModel {
        static get definition() {
            return definition
        }

        constructor(map) {
            this.#map = map
        }

        disabled(node, prop) {
            const {disabled} = definition[prop] ?? {}

            if (disabled) {
                return disabled(this.#map, node)
            }
            else {
                return false
            }
        }

        hidden(node, prop) {
            const {hidden} = definition[prop] ?? {}

            if (hidden) {
                return hidden(this.#map, node)
            }
            else {
                return false
            }
        }

        name(node, prop) {
            const {name} = definition[prop] ?? {}

            if (name) {
                return name(this.#map, node)
            }
            else {
                return ''
            }
        }

        readableValue(node, prop) {
            const {readableValue} = definition[prop] ?? {}

            if (readableValue) {
                return readableValue(this.#map, node)
            }
            else {
                return node.data[prop]
            }
        }

        readOnly(node, prop) {
            const {readOnly} = definition[prop] ?? {}

            if (readOnly) {
                return readOnly(this.#map, node)
            }
            else {
                return false
            }
        }

        required(node, prop) {
            const {required} = definition[prop] ?? {}

            if (required) {
                return required(this.#map, node)
            }
            else {
                return false
            }
        }

        validate(node, prop) {
            const {bizNodeType} = node.data

            const validateProp = prop => {
                if (definition[prop]?.validate) {
                    return definition[prop]?.validate(this.#map, node)
                }

                const v = node.data[prop]
                const name = this.name(node, prop)

                if (
                    this.required(node, prop) &&
                    ! this.hidden(node, prop) &&
                    ! this.disabled(node, prop) &&
                    ! this.readOnly(node, prop) &&
                    ! (v || 0 === v)
                ) {
                    return `${name}不能为空`
                }

                if (v) {
                    const maxLen = lengths[bizNodeType]?.[prop]

                    if (maxLen && maxLen < v.length) {
                        return `${name}最多${maxLen}个字符`
                    }
                }

                return ''
            }

            if (prop) {
                return validateProp(prop)
            }
            else {
                for (const prop of Object.keys(definition)) {
                    const errMsg = validateProp(prop)

                    if (errMsg) {
                        return errMsg
                    }
                }
            }
        }

        #map
    }
}

export const ModelContext = createContext(bizNodeModel())
export const useModel = () => useContext(ModelContext)
export default bizNodeModel
